/**
 * Active Notices View Component
 * Manage mandatory alerts (water outage, etc.) that guests must acknowledge before check-in
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  Volume2,
  VolumeX,
  FileText,
  Users,
  Clock,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  Info,
  BookOpen,
  Timer,
  Activity,
  Send,
  MessageCircle
} from "lucide-react";

export default function ActiveNoticesView({ API }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("notices"); // "notices" or "logbook"
  const [showDialog, setShowDialog] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingAcceptances, setViewingAcceptances] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [loadingAcceptances, setLoadingAcceptances] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyType, setNotifyType] = useState('update');
  const [notifying, setNotifying] = useState(false);
  
  const [form, setForm] = useState({
    title: "",
    message: "",
    notice_type: "warning",
    voice_enabled: true,
    voice_message: "",
    require_acceptance: true,
    active: false,
    estimated_duration: "",
    estimated_end_time: ""
  });

  // Logbook state
  const [logs, setLogs] = useState([]);
  const [logSummary, setLogSummary] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetchNotices();
  }, []);

  useEffect(() => {
    if (activeTab === 'logbook') {
      fetchLogs();
      fetchLogSummary();
    }
  }, [activeTab]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/active-notices`);
      setNotices(res.data || []);
    } catch (err) {
      console.error("Failed to fetch notices:", err);
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoadingLogs(true);
      const res = await axios.get(`${API}/admin/notice-logs`);
      setLogs(res.data || []);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchLogSummary = async () => {
    try {
      const res = await axios.get(`${API}/admin/notice-logs/summary`);
      setLogSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch log summary:", err);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.message) {
      toast.error("Please fill in title and message");
      return;
    }

    try {
      if (editingNotice) {
        await axios.put(`${API}/admin/active-notices/${editingNotice.id}`, form);
        toast.success("Notice updated");
      } else {
        await axios.post(`${API}/admin/active-notices`, form);
        toast.success("Notice created");
      }
      setShowDialog(false);
      setEditingNotice(null);
      resetForm();
      fetchNotices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save notice");
    }
  };

  const handleToggle = async (noticeId) => {
    try {
      const res = await axios.put(`${API}/admin/active-notices/${noticeId}/toggle`);
      toast.success(res.data.message);
      fetchNotices();
    } catch (err) {
      toast.error("Failed to toggle notice");
    }
  };

  const handleDelete = async (noticeId) => {
    try {
      await axios.delete(`${API}/admin/active-notices/${noticeId}`);
      toast.success("Notice deleted");
      setDeleteConfirm(null);
      fetchNotices();
    } catch (err) {
      toast.error("Failed to delete notice");
    }
  };

  const handleNotifyGuests = async () => {
    if (!showNotifyDialog || !notifyMessage) {
      toast.error("Please enter a message");
      return;
    }
    
    setNotifying(true);
    try {
      const res = await axios.post(`${API}/admin/active-notices/${showNotifyDialog.id}/notify-guests`, {
        message: notifyMessage,
        notification_type: notifyType
      });
      toast.success(`${res.data.notified} guests notified via Telegram!`);
      setShowNotifyDialog(null);
      setNotifyMessage('');
      setNotifyType('update');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to notify guests");
    } finally {
      setNotifying(false);
    }
  };

  const handleNotifyResolved = async (notice) => {
    try {
      const res = await axios.post(`${API}/admin/active-notices/${notice.id}/notify-resolved`);
      toast.success(`${res.data.notified} guests notified that the issue is resolved!`);
    } catch (err) {
      toast.error("Failed to notify guests");
    }
  };

  const handleEdit = (notice) => {
    setEditingNotice(notice);
    setForm({
      title: notice.title || "",
      message: notice.message || "",
      notice_type: notice.notice_type || "warning",
      voice_enabled: notice.voice_enabled ?? true,
      voice_message: notice.voice_message || "",
      require_acceptance: notice.require_acceptance ?? true,
      active: notice.active ?? false,
      estimated_duration: notice.estimated_duration || "",
      estimated_end_time: notice.estimated_end_time || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setForm({
      title: "",
      message: "",
      notice_type: "warning",
      voice_enabled: true,
      voice_message: "",
      require_acceptance: true,
      active: false,
      estimated_duration: "",
      estimated_end_time: ""
    });
  };

  const viewAcceptances = async (notice) => {
    setViewingAcceptances(notice);
    setLoadingAcceptances(true);
    try {
      const res = await axios.get(`${API}/admin/active-notices/${notice.id}/acceptances`);
      setAcceptances(res.data || []);
    } catch (err) {
      toast.error("Failed to load acceptances");
    } finally {
      setLoadingAcceptances(false);
    }
  };

  const downloadProof = async (noticeId, guestName) => {
    try {
      const res = await axios.get(`${API}/admin/active-notices/${noticeId}/proof/${encodeURIComponent(guestName)}`);
      const proof = res.data;
      
      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(proof, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `acceptance_proof_${guestName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Proof document downloaded");
    } catch (err) {
      toast.error("Failed to generate proof");
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-amber-400" />;
      case 'info': return <Info className="w-5 h-5 text-blue-400" />;
      default: return <AlertCircle className="w-5 h-5 text-amber-400" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      critical: "bg-red-500/20 text-red-400 border-red-500/30",
      warning: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      info: "bg-blue-500/20 text-blue-400 border-blue-500/30"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs border font-medium ${styles[type] || styles.warning}`}>
        {type?.toUpperCase() || 'WARNING'}
      </span>
    );
  };

  // Get currently active notice
  const activeNotice = notices.find(n => n.active);

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
          Active Notices
        </h1>
        <p className="text-vault-text-secondary font-manrope mt-1">
          Mandatory alerts that guests must acknowledge before check-in
        </p>
      </div>

      {/* Current Active Notice Status */}
      {activeNotice ? (
        <Card className="bg-red-900/30 border-red-500/50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-500/30 rounded-xl flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-red-400 font-semibold text-lg">{activeNotice.title}</p>
                  <p className="text-red-300/70 text-sm">
                    Active since {new Date(activeNotice.created_at).toLocaleDateString()} • 
                    {activeNotice.acceptance_count || 0} acceptances
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowNotifyDialog(activeNotice)}
                  variant="outline"
                  className="border-blue-500 text-blue-400"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Update
                </Button>
                <Button
                  onClick={async () => {
                    await handleNotifyResolved(activeNotice);
                    handleToggle(activeNotice.id);
                  }}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Resolve & Notify
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-green-900/20 border-green-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-semibold">No Active Notices</p>
                <p className="text-green-300/70 text-sm">
                  Guests can check in without additional acknowledgments
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-vault-border pb-4">
        <Button
          variant={activeTab === 'notices' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('notices')}
          className={activeTab === 'notices' ? 'bg-vault-gold text-black' : 'text-vault-text-secondary'}
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Manage Notices
        </Button>
        <Button
          variant={activeTab === 'logbook' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('logbook')}
          className={activeTab === 'logbook' ? 'bg-vault-gold text-black' : 'text-vault-text-secondary'}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Outage Logbook
        </Button>
      </div>

      {/* Notices Tab */}
      {activeTab === 'notices' && (
        <>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Button
          onClick={() => { resetForm(); setEditingNotice(null); setShowDialog(true); }}
          className="bg-vault-gold hover:bg-vault-gold/80 text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Notice
        </Button>
        
        {/* Quick toggle buttons for common scenarios */}
        <Button
          variant="outline"
          onClick={() => {
            setForm({
              title: "City Infrastructure Failure",
              message: "Water is currently out due to city maintenance. We apologize for the inconvenience. Essential services may be limited.",
              notice_type: "critical",
              voice_enabled: true,
              voice_message: "Attention: City water is currently out. Please acknowledge this notice to continue with check-in.",
              require_acceptance: true,
              active: true,
              estimated_duration: "",
              estimated_end_time: ""
            });
            setShowDialog(true);
          }}
          className="border-red-500 text-red-400"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Water Outage
        </Button>
        
        <Button
          variant="outline"
          onClick={() => {
            setForm({
              title: "Scheduled Maintenance",
              message: "Maintenance work is being performed on the property. Some amenities may be temporarily unavailable.",
              notice_type: "warning",
              voice_enabled: true,
              voice_message: "Please note: Scheduled maintenance is in progress. Acknowledge this notice to continue.",
              require_acceptance: true,
              active: true,
              estimated_duration: "",
              estimated_end_time: ""
            });
            setShowDialog(true);
          }}
          className="border-amber-500 text-amber-400"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Maintenance
        </Button>
      </div>

      {/* Notices Table */}
      <Card className="bg-vault-surface border-vault-border">
        <CardHeader>
          <CardTitle className="text-vault-text flex items-center gap-2">
            <FileText className="w-5 h-5 text-vault-gold" />
            All Notices
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold">Status</TableHead>
                  <TableHead className="text-vault-gold">Type</TableHead>
                  <TableHead className="text-vault-gold">Title</TableHead>
                  <TableHead className="text-vault-gold">Est. Duration</TableHead>
                  <TableHead className="text-vault-gold">Acceptances</TableHead>
                  <TableHead className="text-vault-gold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-vault-gold" />
                    </TableCell>
                  </TableRow>
                ) : notices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-vault-text-secondary">
                      No notices created yet
                    </TableCell>
                  </TableRow>
                ) : (
                  notices.map((notice) => (
                    <TableRow key={notice.id} className="border-vault-border hover:bg-vault-surface-highlight">
                      <TableCell>
                        {notice.active ? (
                          <span className="flex items-center gap-1 text-green-400">
                            <Power className="w-4 h-4" />
                            <span className="text-xs font-medium">ACTIVE</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-vault-text-secondary">
                            <PowerOff className="w-4 h-4" />
                            <span className="text-xs">OFF</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{getTypeBadge(notice.notice_type)}</TableCell>
                      <TableCell className="text-vault-text font-medium max-w-[200px] truncate">
                        {notice.title}
                      </TableCell>
                      <TableCell className="text-vault-text-secondary text-sm">
                        {notice.estimated_duration || notice.estimated_end_time 
                          ? (
                            <div className="flex items-center gap-1">
                              <Timer className="w-3 h-3 text-vault-gold" />
                              <span>{notice.estimated_duration || new Date(notice.estimated_end_time).toLocaleTimeString()}</span>
                            </div>
                          ) 
                          : <span className="text-vault-text-secondary/50">Not set</span>
                        }
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewAcceptances(notice)}
                          className="text-vault-text hover:text-vault-gold"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          {notice.acceptance_count || 0}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(notice.id)}
                            className={notice.active ? "text-red-400 hover:text-red-300" : "text-green-400 hover:text-green-300"}
                            title={notice.active ? "Turn OFF" : "Turn ON"}
                          >
                            {notice.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(notice)}
                            className="text-vault-gold hover:text-amber-400"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(notice)}
                            className="text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
      </>
      )}

      {/* Logbook Tab */}
      {activeTab === 'logbook' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          {logSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-vault-surface border-vault-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Power className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-vault-text-secondary text-xs">Total Activations</p>
                      <p className="text-vault-text text-2xl font-bold">{logSummary.total_activations}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-vault-surface border-vault-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <PowerOff className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-vault-text-secondary text-xs">Total Resolved</p>
                      <p className="text-vault-text text-2xl font-bold">{logSummary.total_deactivations}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-vault-surface border-vault-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-vault-text-secondary text-xs">Total Acceptances</p>
                      <p className="text-vault-text text-2xl font-bold">{logSummary.total_acceptances}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-vault-surface border-vault-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-vault-text-secondary text-xs">Completed Outages</p>
                      <p className="text-vault-text text-2xl font-bold">{logSummary.completed_outages?.length || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Completed Outages Summary */}
          {logSummary?.completed_outages?.length > 0 && (
            <Card className="bg-vault-surface border-vault-border">
              <CardHeader>
                <CardTitle className="text-vault-text flex items-center gap-2">
                  <Timer className="w-5 h-5 text-vault-gold" />
                  Completed Outages Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-vault-border hover:bg-transparent">
                      <TableHead className="text-vault-gold">Notice</TableHead>
                      <TableHead className="text-vault-gold">Duration</TableHead>
                      <TableHead className="text-vault-gold">Guests Affected</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logSummary.completed_outages.map((outage, idx) => (
                      <TableRow key={idx} className="border-vault-border">
                        <TableCell className="text-vault-text font-medium">
                          {outage.notice_title}
                        </TableCell>
                        <TableCell className="text-vault-text-secondary">
                          {outage.duration_hours < 1 
                            ? `${Math.round(outage.duration_hours * 60)} minutes`
                            : `${outage.duration_hours} hours`}
                        </TableCell>
                        <TableCell className="text-vault-text">
                          {outage.acceptances} guests
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Full Activity Log */}
          <Card className="bg-vault-surface border-vault-border">
            <CardHeader>
              <CardTitle className="text-vault-text flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-vault-gold" />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-vault-border hover:bg-transparent">
                      <TableHead className="text-vault-gold">Timestamp</TableHead>
                      <TableHead className="text-vault-gold">Action</TableHead>
                      <TableHead className="text-vault-gold">Notice</TableHead>
                      <TableHead className="text-vault-gold">Type</TableHead>
                      <TableHead className="text-vault-gold">Acceptances</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingLogs ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-vault-gold" />
                        </TableCell>
                      </TableRow>
                    ) : logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-vault-text-secondary">
                          No activity logged yet. Logs will appear when notices are activated/deactivated.
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id} className="border-vault-border hover:bg-vault-surface-highlight">
                          <TableCell className="text-vault-text-secondary text-sm">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {log.action === 'activated' ? (
                              <span className="flex items-center gap-1 text-amber-400">
                                <Power className="w-4 h-4" />
                                <span className="text-xs font-medium">ACTIVATED</span>
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-green-400">
                                <PowerOff className="w-4 h-4" />
                                <span className="text-xs font-medium">DEACTIVATED</span>
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-vault-text font-medium max-w-[200px] truncate">
                            {log.notice_title}
                          </TableCell>
                          <TableCell>
                            {getTypeBadge(log.notice_type)}
                          </TableCell>
                          <TableCell className="text-vault-text">
                            {log.acceptance_count_at_time !== undefined ? log.acceptance_count_at_time : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium">Logbook Purpose</p>
                  <p className="text-blue-200/70 text-sm mt-1">
                    This logbook maintains a permanent record of all notice activations and deactivations. 
                    Use this for compliance documentation and to prove when guests were notified of issues 
                    like water outages. Each entry includes the timestamp and number of guest acknowledgments.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {editingNotice ? 'Edit Notice' : 'Create Notice'}
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              This notice will be shown to guests before check-in.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Notice Title *
              </label>
              <Input
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder="e.g., City Infrastructure Failure"
                className="bg-black/50 border-vault-border text-vault-text"
              />
            </div>
            
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Notice Type
              </label>
              <Select value={form.notice_type} onValueChange={(v) => setForm({...form, notice_type: v})}>
                <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-vault-surface border-vault-border">
                  <SelectItem value="critical">Critical (Red)</SelectItem>
                  <SelectItem value="warning">Warning (Yellow)</SelectItem>
                  <SelectItem value="info">Info (Blue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Message *
              </label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({...form, message: e.target.value})}
                placeholder="Describe the situation guests need to be aware of..."
                className="bg-black/50 border-vault-border text-vault-text min-h-[100px]"
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-vault-gold" />
                <span className="text-vault-text">Play Voice Message</span>
              </div>
              <Switch
                checked={form.voice_enabled}
                onCheckedChange={(checked) => setForm({...form, voice_enabled: checked})}
              />
            </div>
            
            {form.voice_enabled && (
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                  Custom Voice Message (optional)
                </label>
                <Textarea
                  value={form.voice_message}
                  onChange={(e) => setForm({...form, voice_message: e.target.value})}
                  placeholder="Leave blank to auto-generate from title and message..."
                  className="bg-black/50 border-vault-border text-vault-text min-h-[60px]"
                />
              </div>
            )}
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-vault-gold" />
                <span className="text-vault-text">Require "I Accept" Button</span>
              </div>
              <Switch
                checked={form.require_acceptance}
                onCheckedChange={(checked) => setForm({...form, require_acceptance: checked})}
              />
            </div>

            {/* Estimated Duration Section */}
            <div className="border-t border-vault-border pt-4 mt-2">
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block flex items-center gap-2">
                <Timer className="w-4 h-4" />
                Estimated Duration
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-vault-text-secondary mb-1 block">Duration Text</label>
                  <Input
                    value={form.estimated_duration}
                    onChange={(e) => setForm({...form, estimated_duration: e.target.value})}
                    placeholder="e.g., 2 hours, Until 5 PM"
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-text-secondary mb-1 block">Expected End Time</label>
                  <Input
                    type="datetime-local"
                    value={form.estimated_end_time}
                    onChange={(e) => setForm({...form, estimated_end_time: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              <p className="text-vault-text-secondary text-xs mt-2">
                This helps guests know when the issue is expected to be resolved.
              </p>
            </div>
            
            <div className="flex items-center justify-between py-2 border-t border-vault-border pt-4">
              <div className="flex items-center gap-2">
                <Power className="w-4 h-4 text-green-400" />
                <span className="text-vault-text font-semibold">Activate Now</span>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(checked) => setForm({...form, active: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-vault-gold hover:bg-vault-gold/80 text-black">
              {editingNotice ? 'Update Notice' : 'Create Notice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Acceptances Dialog */}
      <Dialog open={!!viewingAcceptances} onOpenChange={() => setViewingAcceptances(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
              <Users className="w-5 h-5 text-vault-gold" />
              Acceptances - {viewingAcceptances?.title}
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              Guests who acknowledged this notice
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] mt-4">
            {loadingAcceptances ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-vault-gold" />
              </div>
            ) : acceptances.length === 0 ? (
              <p className="text-vault-text-secondary text-center py-8">
                No acceptances recorded yet
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-vault-border">
                    <TableHead className="text-vault-gold">Guest Name</TableHead>
                    <TableHead className="text-vault-gold">Employee #</TableHead>
                    <TableHead className="text-vault-gold">Telegram</TableHead>
                    <TableHead className="text-vault-gold">Accepted At</TableHead>
                    <TableHead className="text-vault-gold text-right">Proof</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {acceptances.map((acc) => (
                    <TableRow key={acc.id} className="border-vault-border">
                      <TableCell className="text-vault-text font-medium">
                        {acc.guest_name}
                      </TableCell>
                      <TableCell className="text-vault-text-secondary">
                        {acc.employee_number || '-'}
                      </TableCell>
                      <TableCell>
                        {acc.telegram_username ? (
                          <span className="text-blue-400 flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {acc.telegram_username}
                          </span>
                        ) : (
                          <span className="text-vault-text-secondary/50">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-vault-text-secondary text-sm">
                        {new Date(acc.accepted_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadProof(viewingAcceptances.id, acc.guest_name)}
                          className="text-green-400 hover:text-green-300"
                          title="Download Proof"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Notice?</DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              This will permanently delete "{deleteConfirm?.title}". Acceptance records will be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Guests Dialog */}
      <Dialog open={!!showNotifyDialog} onOpenChange={() => setShowNotifyDialog(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-400" />
              Send Update to Guests
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              Notify all guests who provided their Telegram about this notice update.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Notification Type
              </label>
              <Select value={notifyType} onValueChange={setNotifyType}>
                <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-vault-surface border-vault-border">
                  <SelectItem value="update">📢 General Update</SelectItem>
                  <SelectItem value="extended">⏰ Time Extended</SelectItem>
                  <SelectItem value="resolved">✅ Issue Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                Message to Guests *
              </label>
              <Textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="e.g., Water service has been partially restored. Full service expected within the hour."
                className="bg-black/50 border-vault-border text-vault-text min-h-[100px]"
              />
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-400 text-sm flex items-center gap-2">
                <Send className="w-4 h-4" />
                This will send a Telegram message to all guests who provided their username when accepting this notice.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNotifyDialog(null)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button 
              onClick={handleNotifyGuests} 
              disabled={notifying || !notifyMessage}
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              {notifying ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Notification</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
